/**
 * Die Bestandsansicht: die Befunde eines Bestandslaufs **nach Projekt und Pfad**
 * (Spec v1.1, User Stories 116 und 117).
 *
 * Sie ist der Weg zu einem Ertrag, der sonst nur in der Datenbank läge: Der
 * Revisionsstrom sortiert nach Revisionen, und die Befunde eines Bestandslaufs
 * hängen alle an derselben. Gefragt wird deshalb nach der Datei — welche tragen
 * Befunde, wie viele und welchen Schweregrads —, und erst auf Zuruf, welche
 * Befunde eine einzelne Datei trägt.
 *
 * Drei Filter, die drei Achsen eines Bestandslaufs: **Kontrollmuster**,
 * **Schweregrad**, **Status**. Der auf das Muster ist der wichtigste — zusammen
 * mit dem Status beantwortet er, ob ein Muster richtig geschnitten ist, indem er
 * zeigt, wie oft es „zutreffend, unerheblich" bekommen hat. Gefiltert wird im
 * Server: Die Oberfläche hält keine zweite, eigene Liste, aus der sie
 * herausrechnet, sonst stimmte die Seitenzahl nicht mehr mit dem überein, was zu
 * sehen ist.
 *
 * **Bestandsbefunde stehen ohne Abgabe da** (ADR-0010). Dass hier kein Befund
 * aus dem Revisionsstrom auftaucht, der im geschlossenen Modus noch verborgen
 * ist, entscheidet der Server über die Auftragsart — nicht diese Datei.
 *
 * Von einem Befund führt ein Weg in die Datei im Stand seiner Revision. Der
 * Anker ist Projekt, Pfad, Revision und Zeilenbereich (ADR-0006); die Datei
 * kommt aus dem SVN, denn zu einem Bestandsbefund gibt es keine Dateiänderung
 * mit abgelegtem Inhalt.
 *
 * **Zwei Abschnitte, keine Karte.** Die Dateiliste und die Befunde der geöffneten
 * Datei stehen beide über die volle Breite, getrennt durch eine Haarlinie —
 * dieselbe Bauform wie die Listen daneben und wie die Revisionsansicht. Das
 * Design-System sagt es so: „Record sections are full-bleed …, divided by a
 * single top rule, never boxed."
 *
 * **Ohne Werkzeugleiste** (#41, #136): Sie zeigt an und bietet nichts, was auf
 * den ganzen Screen wirkt — die drei Achsen bleiben Filter im Inhalt, und
 * „Schließen"/„Im Zusammenhang" gehören zur geöffneten Datei bzw. zum einzelnen
 * Befund und nicht zum Screen als Ganzem
 * (`docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`).
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, DataTable, Dialog, Modal, Select, StatusPill } from "../../design-system";
import type {
  ControlPatternOption,
  Finding,
  FindingStatus,
  InventoryApi,
  InventoryFile,
  InventoryFiles,
  InventoryFindings,
  InventoryQuery,
  ProjectFile,
  ProjectOption,
  Qualification,
  Severity,
} from "../../api/client";
import { formatNumber } from "../../formatting";
import {
  QUALIFICATION_LABELS,
  QUALIFICATION_TONES,
  SEVERITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
} from "../findings";
import { NumericCell, StrongCell } from "../cells";
import { FilterReset, ListPage, ListSection } from "../list";
import { Notice, describe, useLoaded, type Loaded } from "../loading";

/** Eine Bildschirmhöhe voll Dateien. Der Bestand kommt nie im Ganzen. */
const PER_PAGE = 25;

/** Die Befunde **einer** Datei — mehr öffnet diese Ansicht nicht auf einmal. */
const FINDINGS_PER_PAGE = 200;

const ANY_PATTERN = "Muster: alle";
const ANY_SEVERITY = "Schwere: alle";
const ANY_STATUS = "Status: alle";
const ANY_QUALIFICATION = "Bewertung: alle";

const NO_PROJECTS = "Es ist kein Projekt angelegt.";
const NOTHING = "Für dieses Projekt steht kein Bestandsbefund. Ein Bestandslauf wird in der Verwaltung angestoßen.";

/** Die Reihenfolge, in der die Schweregrade stehen: von schwer nach leicht. */
const SEVERITIES: Severity[] = ["schwer", "mittel", "hinweis"];

/** Die Reihenfolge des Lebenszyklus im Filter: der Weg eines Befundes. */
const STATUSES: FindingStatus[] = ["open", "fixed", "dismissed", "superseded"];

/**
 * Die Qualifizierung steht als **eigener Filter** daneben (Spec v1.4, Story
 * 252): Sie ist die zweite Achse, und über eine Liste gemischt wäre „bestätigt
 * und offen" nicht zu bestellen.
 */
const QUALIFICATIONS: Qualification[] = ["confirmed", "acknowledged", "rejected"];

/** Die gesetzten Filter. `null` heißt bei jedem: danach wird nicht gefiltert. */
interface Chosen {
  pattern: string | null;
  severity: Severity | null;
  status: FindingStatus | null;
  qualification: Qualification | null;
}

const NOTHING_CHOSEN: Chosen = {
  pattern: null,
  severity: null,
  status: null,
  qualification: null,
};

export function InventoryView({ client }: { client: InventoryApi }): ReactElement {
  const projects = useLoaded(() => client.monitoredProjects());
  const [projectId, setProjectId] = useState<number | null>(null);
  const [chosen, setChosen] = useState<Chosen>(NOTHING_CHOSEN);
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState<InventoryFile | null>(null);

  const known = projects.state.kind === "loaded" ? projects.state.value : [];
  // Das erste Projekt ist die Vorauswahl: Eine Ansicht, die erst nach einer
  // Wahl etwas zeigt, sieht aus wie eine leere.
  const chosenProject = known.find((one) => one.id === projectId) ?? known[0] ?? null;

  const query: InventoryQuery = {
    page,
    perPage: PER_PAGE,
    pattern: chosen.pattern,
    severity: chosen.severity,
    status: chosen.status,
    qualification: chosen.qualification,
    path: null,
  };

  const files = useAnswer<InventoryFiles | null>(
    () =>
      chosenProject === null
        ? Promise.resolve(null)
        : client.inventoryFiles(chosenProject.id, query),
    JSON.stringify([chosenProject?.id ?? null, query]),
  );

  // Ein geänderter Filter beginnt wieder auf der ersten Seite und schließt die
  // geöffnete Datei: Seite drei eines anderen Filters ist eine andere Seite drei.
  const change = (next: Partial<Chosen>): void => {
    setChosen((previous) => ({ ...previous, ...next }));
    setPage(1);
    setOpened(null);
  };

  const listed = files.kind === "loaded" ? files.value : null;
  const pages = listed?.pages ?? 1;
  const total = listed?.total ?? 0;

  return (
    <ListPage>
      <ListSection
        title="Bestandsbefunde"
        glyph="scan-search"
        first
        headerRight={listed === null ? null : <Chip>{`${formatNumber(total)} Dateien`}</Chip>}
        filters={
          <FilterBar
            projects={known}
            project={chosenProject}
            onProject={(id) => {
              setProjectId(id);
              setPage(1);
              setOpened(null);
            }}
            patterns={listed?.patterns ?? []}
            chosen={chosen}
            change={change}
          />
        }
        paging={{ page, pages, perPage: PER_PAGE, total, onTurn: setPage }}
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {files.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {files.kind === "failed" ? (
            <Notice text={files.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {projects.state.kind === "failed" ? (
            <Notice text={projects.state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {projects.state.kind === "loaded" && known.length === 0 ? (
            <Notice text={NO_PROJECTS} />
          ) : null}
          {listed !== null && listed.files.length === 0 ? <Notice text={NOTHING} /> : null}
        </div>

        {listed !== null && listed.files.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setOpened(listed.files[index] ?? null)}
            columns={[
              { key: "path", label: "Datei", maxWidth: 520 },
              { key: "revision", label: "Revision", align: "right", width: 100 },
              { key: "count", label: "Befunde", align: "right", width: 90 },
              { key: "severities", label: "Schweregrade", width: 260 },
            ]}
            rows={listed.files.map((file) => ({
              cells: {
                path: <StrongCell>{file.path}</StrongCell>,
                revision: <NumericCell>{`r${formatNumber(file.revisionNumber)}`}</NumericCell>,
                count: <NumericCell>{formatNumber(file.total)}</NumericCell>,
                severities: <SeverityPills file={file} />,
              },
            }))}
          />
        ) : null}
      </ListSection>

      {chosenProject === null || opened === null ? null : (
        <FilePanel
          key={`${chosenProject.id}:${opened.path}:${opened.revisionNumber}`}
          client={client}
          projectId={chosenProject.id}
          file={opened}
          chosen={chosen}
          onClose={() => setOpened(null)}
        />
      )}
    </ListPage>
  );
}

/**
 * Die Filterleiste: das Projekt und die drei Achsen.
 *
 * Die Muster kommen aus derselben Antwort wie die Zeilen und bleiben stehen,
 * auch wenn gerade gefiltert wird — sonst nähme ein gesetzter Filter die Wege
 * zurück, ihn zu wechseln.
 */
function FilterBar({
  projects,
  project,
  onProject,
  patterns,
  chosen,
  change,
}: {
  projects: ProjectOption[];
  project: ProjectOption | null;
  onProject: (id: number) => void;
  patterns: ControlPatternOption[];
  chosen: Chosen;
  change: (next: Partial<Chosen>) => void;
}): ReactElement {
  const labelled = patterns.find((one) => one.identifier === chosen.pattern);

  return (
    <div
      role="group"
      aria-label="Filter"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-4)",
        padding: "var(--sp-5) var(--sp-6)",
        borderBottom: "1px solid var(--line-f)",
      }}
    >
      {/*
        Die Breiten der Filterfelder bleiben die gemessenen und wandern nur vom
        eigenen Prop des abgelösten Auswahlfelds in dessen `style`: Das V3-Feld
        führt kein `width`. Ein Token gibt es dafür in keiner der beiden
        Generationen — die Zahl ist je Filter am längsten Eintrag abgelesen, und
        ein Token je Filter wäre keines. Zur Bemessung siehe #140.
      */}
      <Select
        style={{ width: 220 }}
        height={28}
        value={project?.name ?? "Kein Projekt"}
        options={projects.map((one) => one.name)}
        onSelect={(picked) => {
          const found = projects.find((one) => one.name === picked);

          if (found !== undefined) {
            onProject(found.id);
          }
        }}
      />
      <Select
        style={{ width: 260 }}
        height={28}
        value={labelled?.label ?? ANY_PATTERN}
        options={[ANY_PATTERN, ...patterns.map((one) => one.label)]}
        onSelect={(picked) =>
          change({ pattern: patterns.find((one) => one.label === picked)?.identifier ?? null })
        }
      />
      <Select
        style={{ width: 170 }}
        height={28}
        value={chosen.severity ?? ANY_SEVERITY}
        options={[ANY_SEVERITY, ...SEVERITIES]}
        onSelect={(picked) =>
          change({ severity: SEVERITIES.find((one) => one === picked) ?? null })
        }
      />
      <Select
        style={{ width: 220 }}
        height={28}
        value={chosen.status === null ? ANY_STATUS : STATUS_LABELS[chosen.status]}
        options={[ANY_STATUS, ...STATUSES.map((one) => STATUS_LABELS[one])]}
        onSelect={(picked) =>
          change({ status: STATUSES.find((one) => STATUS_LABELS[one] === picked) ?? null })
        }
      />
      <Select
        style={{ width: 220 }}
        height={28}
        value={
          chosen.qualification === null
            ? ANY_QUALIFICATION
            : QUALIFICATION_LABELS[chosen.qualification]
        }
        options={[ANY_QUALIFICATION, ...QUALIFICATIONS.map((one) => QUALIFICATION_LABELS[one])]}
        onSelect={(picked) =>
          change({
            qualification:
              QUALIFICATIONS.find((one) => QUALIFICATION_LABELS[one] === picked) ?? null,
          })
        }
      />
      {/*
        Am Ende der Leiste, hinter der letzten Achse: Damit wandert er beim
        Umbrechen mit und schiebt keine Achse aus ihrer Position.

        Das **Projekt bleibt stehen** — es steht nicht in `Chosen` und wird
        deshalb weder verglichen noch zurückgestellt: Es hat in dieser Leiste
        keine Wahl „alle", es ist der Gegenstand der Ansicht und nicht ein Filter
        über sie.
      */}
      <FilterReset
        chosen={chosen}
        start={NOTHING_CHOSEN}
        onReset={() => change(NOTHING_CHOSEN)}
      />
    </div>
  );
}

/**
 * Wie viele Befunde welchen Schweregrads eine Datei trägt. Eine Null steht nicht da.
 *
 * „hinweis" trägt keine Farbe und steht deshalb als `Chip` da (#141) — eine
 * Zählung ohne Warnung ist ein Etikett und kein Zustand.
 */
function SeverityPills({ file }: { file: InventoryFile }): ReactElement {
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "var(--sp-3)" }}>
      {SEVERITIES.filter((severity) => file.severities[severity] > 0).map((severity) => {
        const tone = SEVERITY_TONES[severity];
        const label = `${formatNumber(file.severities[severity])} ${severity}`;

        return tone === null ? (
          <Chip key={severity}>{label}</Chip>
        ) : (
          <StatusPill key={severity} tone={tone}>
            {label}
          </StatusPill>
        );
      })}
    </span>
  );
}

/**
 * Die Befunde einer Datei, mit ihrem Zeilenbereich und beiden Angaben.
 *
 * **Gelesen und nicht gesetzt** (ADR-0022): Hier standen einmal die drei
 * Ausgänge des Qualifizierens, je Befund, über einen eigenen Schreibweg. Beides
 * ist weg — bewertet wird an der **Stelle**, mit einem Handgriff, der beide
 * Achsen setzt, und dort, wo der Code steht. Eine Liste zeigt das Ergebnis und
 * setzt nichts; „Fehlalarm" aus einer Tabellenzeile heraus wäre ein Klick ins
 * Blaue.
 *
 * Die gesetzten Filter gelten hier weiter: Wer nach einem Muster filtert und eine
 * Datei öffnet, will die Fundstellen dieses Musters sehen und nicht alle.
 *
 * **Ein Abschnitt und keine Karte.** Er steht unter der Dateiliste, über die
 * volle Breite und nur durch eine Haarlinie von ihr getrennt — dieselbe Bauform
 * wie die Liste darüber und wie die Revisionsansicht. Als Kasten unter einer
 * randlosen Tabelle sah er aus wie ein Fremdkörper, und der Rahmen nahm dem
 * Befundtext auf jeder Seite Rand, den er als Fließtext braucht.
 */
function FilePanel({
  client,
  projectId,
  file,
  chosen,
  onClose,
}: {
  client: InventoryApi;
  projectId: number;
  file: InventoryFile;
  chosen: Chosen;
  onClose: () => void;
}): ReactElement {
  const [shown, setShown] = useState<Finding | null>(null);

  const found = useAnswer<InventoryFindings>(
    () =>
      client.inventoryFindings(projectId, {
        page: 1,
        perPage: FINDINGS_PER_PAGE,
        pattern: chosen.pattern,
        severity: chosen.severity,
        status: chosen.status,
        qualification: chosen.qualification,
        path: file.path,
      }),
    JSON.stringify([projectId, file.path, chosen]),
  );

  const findings = found.kind === "loaded" ? found.value.findings : [];

  return (
    <>
      <ListSection
        title={file.path}
        glyph="file-code"
        headerRight={
          <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              {`Stand r${formatNumber(file.revisionNumber)}`}
            </span>
            <Button variant="outline" size="sm" onClick={onClose}>
              Schließen
            </Button>
          </span>
        }
      >
        <div style={{ padding: "0 var(--sp-5)" }}>
          {found.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {found.kind === "failed" ? (
            <Notice text={found.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {found.kind === "loaded" && findings.length === 0 ? (
            <Notice text="Zu dieser Datei steht unter den gesetzten Filtern kein Befund." />
          ) : null}
        </div>

        {findings.map((finding) => (
          <FindingRow
            key={finding.id}
            finding={finding}
            onShow={() => setShown(finding)}
          />
        ))}
      </ListSection>

      {shown === null ? null : (
        <FileDialog
          client={client}
          projectId={projectId}
          finding={shown}
          onClose={() => setShown(null)}
        />
      )}
    </>
  );
}

/**
 * Ein Befund: Anker, Qualifizierung, Text — und die Ausgänge.
 *
 * Über die volle Breite und durch eine Haarlinie **über** sich getrennt: Die
 * Linie trennt zwei Befunde und schließt keinen ab — der letzte bekam sonst
 * einen Strich, unter dem nichts mehr kommt.
 */
function FindingRow({
  finding,
  onShow,
}: {
  finding: Finding;
  onShow: () => void;
}): ReactElement {
  // Farbe oder keine — dieselbe Fallunterscheidung wie überall seit #141: Ein
  // Ton macht die Angabe zum Zustand, kein Ton lässt sie ein Etikett sein.
  const severityTone = SEVERITY_TONES[finding.severity];
  const statusTone = STATUS_TONES[finding.status];
  const qualificationTone = finding.qualification === null ? null : QUALIFICATION_TONES[finding.qualification];

  return (
    <div
      aria-label={`Befund ${lineRangeOf(finding)}`}
      role="group"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--sp-4)",
        padding: "var(--sp-5)",
        borderTop: "1px solid var(--line-f)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "var(--sp-4)" }}>
        <span
          style={{
            fontSize: "var(--fs-11)",
            color: "var(--fg-1)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {lineRangeOf(finding)}
        </span>
        <StatusPill tone="blue">{finding.kind}</StatusPill>
        {severityTone === null ? (
          <Chip>{finding.severity}</Chip>
        ) : (
          <StatusPill tone={severityTone}>{finding.severity}</StatusPill>
        )}
        {statusTone === null ? (
          <Chip>{STATUS_LABELS[finding.status]}</Chip>
        ) : (
          <StatusPill tone={statusTone}>{STATUS_LABELS[finding.status]}</StatusPill>
        )}
        {/* Beide Angaben nebeneinander, ohne dass die eine die andere verdeckt
            (Spec v1.4, Story 253). Ohne Urteil steht hier nichts — „unbewertet"
            wäre ein Wort für einen Zustand, den das Fehlen schon sagt. */}
        {finding.qualification === null ? null : qualificationTone === null ? (
          <Chip>{QUALIFICATION_LABELS[finding.qualification]}</Chip>
        ) : (
          <StatusPill tone={qualificationTone}>{QUALIFICATION_LABELS[finding.qualification]}</StatusPill>
        )}
      </div>

      <span
        style={{
          fontSize: "var(--fs-12)",
          lineHeight: "var(--lh-normal)",
          color: "var(--text-body)",
          textWrap: "pretty",
        }}
      >
        {finding.body}
      </span>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-4)" }}>
        <Button variant="outline" size="sm" onClick={onShow}>
          Im Zusammenhang
        </Button>
      </div>
    </div>
  );
}

/**
 * Die Datei im Stand der Revision, die der Befund nennt, mit markiertem
 * Zeilenbereich.
 *
 * Der Stand kommt aus dem SVN und nicht aus einer Ablage: Zu einem
 * Bestandsbefund gibt es keine Dateiänderung. Antwortet das Repository nicht,
 * steht das hier als Auskunft und nicht als leere Datei.
 */
function FileDialog({
  client,
  projectId,
  finding,
  onClose,
}: {
  client: InventoryApi;
  projectId: number;
  finding: Finding;
  onClose: () => void;
}): ReactElement {
  const { state } = useLoaded<ProjectFile>(() =>
    client.projectFile(projectId, finding.path, finding.revisionNumber),
  );
  const file = state.kind === "loaded" ? state.value : null;

  return (
    <Modal onClose={onClose}>
      <Dialog
        title={finding.path}
        description={`Stand r${formatNumber(finding.revisionNumber)} · ${lineRangeOf(finding)}`}
        width={980}
        onClose={onClose}
        footer={
          <Button variant="outline" size="lg" onClick={onClose}>
            Schließen
          </Button>
        }
      >
        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}

        {file === null ? null : <FileLines content={file.content} finding={finding} />}
      </Dialog>
    </Modal>
  );
}

/** Der Dateiinhalt, Zeile für Zeile; die Zeilen des Befundes sind hervorgehoben. */
function FileLines({ content, finding }: { content: string; finding: Finding }): ReactElement {
  const lines = content.split("\n");

  // Eine Datei endet mit einem Zeilenumbruch; die leere Zeile dahinter ist keine.
  if (lines.length > 1 && lines[lines.length - 1] === "") {
    lines.pop();
  }

  return (
    <div
      className="erp-scroll"
      aria-label="Dateiinhalt"
      style={{
        maxHeight: 480,
        overflow: "auto",
        borderRadius: "var(--r-field)",
        background: "var(--bg-sunken)",
        border: "1px solid var(--line-f)",
      }}
    >
      {lines.map((text, index) => {
        const number = index + 1;
        const marked = number >= finding.startLine && number <= finding.endLine;

        return (
          <div
            key={index}
            aria-label={`Zeile ${number}`}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "var(--sp-4)",
              padding: "0 var(--sp-5)",
              borderLeft: `1px solid ${marked ? "var(--ac-blue)" : "transparent"}`,
              background: marked
                ? "color-mix(in srgb, var(--ac-blue) 26%, transparent)"
                : "transparent",
              color: "var(--text-body)",
              fontSize: "var(--fs-12)",
              lineHeight: "var(--lh-normal)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                flex: "none",
                width: 44,
                textAlign: "right",
                color: "var(--text-meta)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--fs-11)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {number}
            </span>
            <span style={{ whiteSpace: "pre", overflow: "hidden", textOverflow: "ellipsis" }}>
              {text === "" ? " " : text}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Fragt beim Erscheinen und bei jeder geänderten Frage neu.
 *
 * Die Frage steht als Zeichenkette in der Abhängigkeit: Das Objekt entsteht bei
 * jedem Rendern neu, und ein neues Objekt löste jedes Mal eine neue Anfrage aus.
 */
function useAnswer<T>(ask: () => Promise<T>, asked: string): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ kind: "loading" });
  const latest = useRef(ask);
  latest.current = ask;

  useEffect(() => {
    let discarded = false;
    setState({ kind: "loading" });

    latest.current().then(
      (value) => {
        if (!discarded) {
          setState({ kind: "loaded", value });
        }
      },
      (error: unknown) => {
        if (!discarded) {
          setState({ kind: "failed", message: describe(error) });
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [asked]);

  return state;
}

/** Der Zeilenbereich eines Befundes, wie ihn ein Mensch liest. */
function lineRangeOf(finding: Finding): string {
  return finding.startLine === finding.endLine
    ? `Zeile ${formatNumber(finding.startLine)}`
    : `Zeilen ${formatNumber(finding.startLine)}–${formatNumber(finding.endLine)}`;
}
